import React from 'react'
import { Pressable, Text, View, type PressableProps } from 'react-native'
import { BigNumber } from 'mathjs'
import { useTranslation } from 'react-i18next'

import { BuyOrderStatus, SellOrderStatus } from '@/constants/common'
import { Mood, OrderType } from '@/types/common'
import { formatTokenAmount, formatTokenAmountRatio, marketPresentation } from '@/config/marketPresentation'
import { formatDate } from '@/utils/formatter'
import { Tag } from '@/components/base'

// TODO: use i18n instead of hardcoding text
const ORDER_STATUS: Record<BuyOrderStatus, Mood> = {
  [BuyOrderStatus.uninitialized]: 'error',
  [BuyOrderStatus.unpaired]: 'neutral',
  [BuyOrderStatus.paired]: 'warning',
  [BuyOrderStatus.paymentMade]: 'info',
  [BuyOrderStatus.completed]: 'success',
  [BuyOrderStatus.canceling]: 'error',
  [BuyOrderStatus.canceled]: 'error',
  [BuyOrderStatus.forceCanceled]: 'error',
  [BuyOrderStatus.complainted]: 'error',
  [BuyOrderStatus.appealing]: 'error'
}

interface OrderCardProps extends PressableProps {
  orderType: OrderType
  orderStatus: BuyOrderStatus
  dealAmount: BigNumber
  actualDealAmount?: BigNumber
  basePrice: number
  createdAt: string
  isActionable?: boolean
}

// TODO: i18n Translation
function OrderCard({
  orderType,
  orderStatus,
  dealAmount,
  actualDealAmount,
  basePrice,
  createdAt,
  isActionable = false,
  ...delegated
}: OrderCardProps) {
  const { t } = useTranslation()
  const hasCompletedAmount = actualDealAmount !== undefined
  const amountDisplayedLabel = t(hasCompletedAmount ? 'orders:completedListedQuantity' : 'orders:transactionQuantity')
  const amountDisplayedValue = hasCompletedAmount
    ? formatTokenAmountRatio(actualDealAmount.toString(), dealAmount.toString())
    : formatTokenAmount(dealAmount.toString())
  return (
    <Pressable className={'rounded-lg border border-border-10 bg-background-primary p-4'} {...delegated}>
      <View className={'flex-row items-start justify-between'}>
        <View className={'gap-0.5'}>
          <Text className={'text-base font-semibold text-text-primary'}>
            {t(orderType === 'buy' ? 'orders:buyToken' : 'orders:sellToken', {
              token: marketPresentation.platformToken.displayName
            })}
          </Text>
          <Text className={'font-roboto-medium text-xs text-text-tertiary'}>{formatDate(createdAt)}</Text>
        </View>
        <View className={'flex-row items-center gap-2'}>
          <Tag mood={ORDER_STATUS[orderStatus]}>
            {t(
              `orderList:tags.${orderType}.${orderType === 'buy' || actualDealAmount === undefined ? BuyOrderStatus[orderStatus] : SellOrderStatus[orderStatus]}`
            )}
          </Tag>
          {isActionable && <View className={'size-1.5 rounded-full bg-danger'} />}
        </View>
      </View>
      <View className={'h-8'} />
      <View className={'flex-row justify-between'}>
        <Text className={'text-sm font-medium text-text-tertiary'}>{amountDisplayedLabel}</Text>
        <Text className={'font-roboto-medium text-sm font-medium text-primary'}>{amountDisplayedValue}</Text>
      </View>
      <View className={'h-4'} />
      <View className={'h-10 items-center justify-center rounded-lg bg-primary-subtle'}>
        <Text className={'font-sm font-medium text-primary'}>{t('orders:viewOrder')}</Text>
      </View>
    </Pressable>
  )
}

export default OrderCard
